import {
  Path,
  Transforms,
  Editor,
  Node
} from 'slate';
import {
  ReactEditor
} from 'slate-react';
import HelptapEditor from '../';
import * as baseNodeUtilities from './base-node';

import elementTypes from '../../elements/types';

export function getDefaultOptions() {

  const options = baseNodeUtilities.getDefaultOptions();

  return options;

}

export function buildNode(type, options = {}) {

  const node = {
    ...getDefaultOptions(),
    ...options,
    type
  };

  if (node.children.length === 0) {
    node.children.push({ text: '' });
  }

  return node;

}

export function buildUnorderedListNode(options) {

  const node = buildNode(elementTypes.BULLETED_LIST, options);

  return node;

}

export function buildOrderedListNode(options) {

  const node = buildNode(elementTypes.NUMBERED_LIST, options);

  return node;

}

export function buildListItemNode(type, options) {

  const node = buildNode(type, options);

  return node;

}

export function buildEmptyListNode(type) {

  const node = buildNode(type);

  node.children = [];

  return node;

}

export function getListItemType(listType) {

  if (listType === elementTypes.BULLETED_LIST) {
    return elementTypes.BULLETED_LIST_ITEM;
  }

  return elementTypes.NUMBERED_LIST_ITEM;

}

export function insertBlock(editor, type, blockOptions) {

  const listItem = {
    type: getListItemType(type),
    children: [{ text: '' }]
  };

  let listNode = {
    type,
    ...baseNodeUtilities.getDefaultOptions(),
    ...blockOptions,
    children: [listItem]
  };

  Transforms.insertNodes(editor, listNode, blockOptions);
  HelptapEditor.moveCursorToStartOfTheLine(editor, blockOptions.at);
  ReactEditor.focus(editor, blockOptions);

}

export function insert(editor, type, insertOptions) {

  HelptapEditor.insertHeading(editor, type, insertOptions);

}

export function toggle(editor, type) {

  const node = buildNode(type);

  HelptapEditor.setNodeOptions(editor, node);

}

export function duplicate(editor, node, nodePath) {

  const clonedNode = HelptapEditor.cloneNode(node);

  HelptapEditor.insertNode(editor, clonedNode, nodePath);

}

export function handleDeleteBackward(editor, node, nodePath) {

  const listNodePath = Path.parent(editor.selection.focus.path);
  const empty = HelptapEditor.isNodeEmptyByPath(editor, listNodePath);

  if (empty === false) {
    return false;
  }

  let paragraphPath = null;

  if (node.children.length === 1) {
    paragraphPath = nodePath;
    HelptapEditor.deleteNodeByPath(editor, nodePath);

    HelptapEditor.paragraph.insert(editor, paragraphPath);
    HelptapEditor.focusAt(editor, [...paragraphPath, 0]);
  } else {
    HelptapEditor.deleteNodeByPath(editor, listNodePath);
    if (Path.hasPrevious(listNodePath) === true) {
      Transforms.select(editor, Editor.end(editor, Path.previous(listNodePath)));
    } else {
      if (Path.hasPrevious(Path.parent(listNodePath)) === true) {
        Transforms.select(editor, Editor.end(editor, Path.previous(Path.parent(listNodePath))));
      } else {
        Transforms.select(editor, Editor.start(editor, listNodePath));
      }
    }
    HelptapEditor.focus(editor);
  }

  return true;
}

export function handleChecklistDeleteBackward(editor, node, nodePath) {

  const textEmpty = HelptapEditor.isEmptyNode(node);

  if (textEmpty === false) {
    return false;
  }

  HelptapEditor.setNodeOptions(editor, { type: elementTypes.DEFAULT }, {});

  return true;

}

export function isEmpty(editor, node) {

  if (node.children.length > 1) {
    return false;
  }

  if (node.children[0].children.length > 1) {
    return false;
  }

  return Editor.isEmpty(editor, node);

}

const listItemTypes = [
  elementTypes.NUMBERED_LIST_ITEM,
  elementTypes.BULLETED_LIST_ITEM
];

export function getClosestListItemPath(editor, fromPath) {

  let currentPath = fromPath;

  while (currentPath.length > 0) {
    const node = Node.get(editor, currentPath);
    if (listItemTypes.includes(node.type) === true) {
      return currentPath;
    }
    currentPath = Path.parent(currentPath);
  }

  return null;

}

function restoreSelectionWithinItem(editor, savedSelection, sourceItemPath, targetItemPath) {

  if (savedSelection === null) {
    Transforms.select(editor, Editor.start(editor, targetItemPath));
    return;
  }

  const anchorSuffix = savedSelection.anchor.path.slice(sourceItemPath.length);
  const focusSuffix = savedSelection.focus.path.slice(sourceItemPath.length);

  Transforms.select(editor, {
    anchor: {
      path: targetItemPath.concat(anchorSuffix),
      offset: savedSelection.anchor.offset
    },
    focus: {
      path: targetItemPath.concat(focusSuffix),
      offset: savedSelection.focus.offset
    }
  });

}

export function nest(editor) {

  if (!editor.selection) {
    return false;
  }

  const itemPath = getClosestListItemPath(editor, editor.selection.anchor.path);

  if (itemPath === null) {
    return false;
  }

  if (Path.hasPrevious(itemPath) === false) {
    return false;
  }

  const parentList = Node.get(editor, Path.parent(itemPath));
  const listType = parentList.type;
  const previousItemPath = Path.previous(itemPath);
  const previousItem = Node.get(editor, previousItemPath);
  const lastChild = previousItem.children[previousItem.children.length - 1];
  const savedSelection = editor.selection;

  let targetPath = null;

  Editor.withoutNormalizing(editor, function moveIntoSublist() {

    if (lastChild.type === listType) {
      const sublistPath = previousItemPath.concat([previousItem.children.length - 1]);
      targetPath = sublistPath.concat([lastChild.children.length]);
      Transforms.moveNodes(editor, { at: itemPath, to: targetPath });
    } else {
      const sublistPath = previousItemPath.concat([previousItem.children.length]);
      Transforms.insertNodes(editor, { type: listType, children: [] }, { at: sublistPath });
      targetPath = sublistPath.concat([0]);
      Transforms.moveNodes(editor, { at: itemPath, to: targetPath });
    }

  });

  restoreSelectionWithinItem(editor, savedSelection, itemPath, targetPath);
  ReactEditor.focus(editor);

  return true;

}

export function outdent(editor) {

  if (!editor.selection) {
    return false;
  }

  const itemPath = getClosestListItemPath(editor, editor.selection.anchor.path);

  if (itemPath === null) {
    return false;
  }

  const parentListPath = Path.parent(itemPath);
  const containerPath = Path.parent(parentListPath);

  if (containerPath.length === 0) {
    return false;
  }

  const container = Node.get(editor, containerPath);

  if (listItemTypes.includes(container.type) === false) {
    return false;
  }

  const targetPath = Path.next(containerPath);
  const savedSelection = editor.selection;

  Editor.withoutNormalizing(editor, function moveOutOfSublist() {
    Transforms.moveNodes(editor, { at: itemPath, to: targetPath });
  });

  restoreSelectionWithinItem(editor, savedSelection, itemPath, targetPath);
  ReactEditor.focus(editor);

  return true;

}